import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback
} from 'react';
import { toLonLat } from 'ol/proj';
import { v4 as uuidv4 } from 'uuid';
import ImageLayer from 'ol/layer/Image';
import { Geometry, Polygon } from 'ol/geom';
import { useParams } from 'react-router-dom';
import GeoImage from 'ol-ext/source/GeoImage';
import { cloneDeep, isEmpty, debounce } from 'lodash';
import { useLocalStorage, useToggle } from 'usehooks-ts';

import { MapConfigStore } from '..';
import {
  UpdateZone,
  GetTaskById,
  GetZonesByIds,
  GetTasksByIds,
  UpdateMapAsync,
  UpdateTaskAsync,
  UpdateAreaAsync,
  GetMapByIdAsync,
  GetAreasWorldMap,
  GetResourceValue,
  AddResourceAsync,
  AddMapEntityAsync,
  AddAreaEntityAsync,
  AddTaskEntityAsync,
  AddZoneEntityAsync,
  UpdateGameDocState
} from '../../../utils/game-document';
import {
  MapEntity,
  AreaEntity,
  ZoneEntity,
  TaskEntity,
  EntityEditor,
  ResourceEntity
} from '../../../types/game-document/';
import {
  DEFAULT_LATITUDE,
  DEFAULT_LONGITUDE
} from '../../../constants/coordinate';
import { Coordinate } from '../../../types/place';
import { uuid } from '../../../types/common-helper';
import { MapImage } from '../../../types/game-document';
import MapEditor from '../../../components/map/map-editor';
import { useGameTask } from '../../../hooks/use-game-task';
import MapCanvas from '../../../components/map/map-canvas';
import { useGameRoute } from '../../../hooks/use-game-route';
import { MapContext } from '../../../components/map/map-context';
import { GameDocumentContext } from '../../../contexts/game-document';
import { AddActionToTask } from '../../../utils/game-document/settings';
import { ZoneAssetId } from '../../../types/game-document/entities/zone';
import { ZoneEditorWindow } from '../../../features/game-document/zones';
import { AreaEditorWindow } from '../../../features/game-document/areas';
import { TaskEditorWindow } from '../../../features/game-document/tasks';
import { repositionImageToMap } from '../../../utils/map/objects-position';
import { GenerateDefaultWorldMap } from '../../../utils/game-document/factory';
import MapIllustrationCanvas from '../../../components/map/map-illustration-canvas';
import { UpdateMapImageAsync } from '../../../utils/game-document/assets/map-image';
import { useGameDocumentResources } from '../../../hooks/use-game-document-resources';
import { useGameTheme } from '../../../hooks/services/use-game-theme';

export type MapsOptions = 'gis' | 'illustrated';
export type MapAction = 'Zone' | 'Task' | 'Area' | 'Annotation' | 'Images' | '';

const WorldMap: React.FC = () => {
  const { gameId } = useParams();
  const [mapConfig] = useLocalStorage<MapConfigStore>(
    `map-config-${gameId}`,
    {}
  );

  const [state, setState] = useContext(GameDocumentContext);
  const gameDocumentFiles = useGameDocumentResources();
  const map = useContext(MapContext);
  const [selectAllAssets, setSelectAllAssets] = useState<string>('');
  const [entityZoneEditorIsVisible, setEntityZoneEditorIsVisible] =
    useState<boolean>(false);
  const [entityAreaEditorIsVisible, setEntityAreaEditorIsVisible] =
    useState<boolean>(false);
  const [entityTaskEditorIsVisible, setEntityTaskEditorIsVisible] =
    useState<boolean>(false);
  const [coordinate, setCoordinate] = useState<Coordinate>();
  const [tempArea, setTempArea] = useState<AreaEntity>({
    id: '',
    name: '',
    description: ''
  });
  const [tempZone, setTempZone] = useState<ZoneEntity>({
    id: '',
    name: '',
    description: ''
  });
  const [tempTask, setTempTask] = useState<TaskEntity>({
    id: '',
    name: '',
    description: ''
  });
  const [selectedPanel, setSelectedPanel] = useState<MapAction>('');
  const [mapInfo, setMapInfo] = useState<MapEntity>();
  const [isShowZoneNameVisibility, setIsShowZoneNameVisibility] =
    useState<boolean>(
      state.gameDocument?.overview?.showZonenameVisibility ?? false
    );
  const [isShowAreaNameVisibility, setIsShowAreaNameVisibility] =
    useState<boolean>(
      state.gameDocument?.overview?.showAreanameVisibility ?? false
    );
  const [vectorPopupId, setVectorPopupId] = React.useState<string>();

  const [showAllZones, setShowAllZones] = useToggle();
  const [showAllAreas, setShowAllAreas] = useToggle();
  const [showTaskTitle, toggleShowTaskTitle] = useToggle(
    mapConfig.showTaskTitle !== undefined ? mapConfig.showTaskTitle : true
  );

  const zones = useMemo(() => {
    if (!state.gameDocument) return [];

    const zoneIds = state.gameDocument?.rules?.worldMap?.zones?.map(
      (zone) => zone.zoneAssId
    );

    const zones = GetZonesByIds(state.gameDocument, zoneIds);
    return zones;
  }, [state.gameDocument]);

  const areas = useMemo(() => {
    if (!state.gameDocument) return [];

    return GetAreasWorldMap(state.gameDocument);
  }, [state.gameDocument]);

  const tasks = useMemo(() => {
    if (!state.gameDocument) return [];

    const taskIds = state.gameDocument?.rules?.worldMap?.tasks?.map(
      (task) => task.taskAssId
    );

    return GetTasksByIds(state.gameDocument, taskIds);
  }, [state.gameDocument]);

  const routes = useMemo(() => {
    if (!state.gameDocument) return [];

    const allRoute = state?.gameDocument?.assets?.routes ?? [];
    const mapAssetId = state?.gameDocument?.rules.worldMap.mapAssId;
    return allRoute.filter((item) => item.mapId === mapAssetId);
  }, [state.gameDocument]);

  const { zoneId } = useParams();

  const toggleZoneEntityEditor = useCallback(() => {
    setEntityZoneEditorIsVisible(!entityZoneEditorIsVisible);
  }, [entityZoneEditorIsVisible]);

  const toggleAreaEntityEditor = () => {
    setEntityAreaEditorIsVisible(!entityAreaEditorIsVisible);
  };

  const toggleTaskEntityEditor = useCallback(() => {
    setEntityTaskEditorIsVisible(!entityTaskEditorIsVisible);
  }, [entityTaskEditorIsVisible]);

  const setToBrowserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setCoordinate({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        });
      });
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  }, []);

  const getWorldMapAsset = useCallback(async () => {
    return await GetMapByIdAsync(
      state?.gameDocument!,
      state?.gameDocument?.rules?.worldMap?.mapAssId!
    );
  }, [state?.gameDocument]);

  const onSelectedPanel = useCallback((MapAction: MapAction) => {
    setSelectedPanel(MapAction);
  }, []);

  const updateWorldMapAsset = useCallback(
    async (mapEntity: MapEntity) => {
      const prevMap = await getWorldMapAsset();

      UpdateMapAsync(state?.gameDocument!, mapEntity.id, mapEntity).then(
        (response) => {
          let updatedGameDocument = cloneDeep(response);

          if (prevMap?.imageResId) {
            const imgDimention = { width: 0, height: 0 };
            const img = new Image();
            img.src = GetResourceValue(response, prevMap.imageResId);
            img.onload = () => {
              imgDimention.width = img.width;
              imgDimention.height = img.height;

              const mapElement = document.getElementById('map');
              let mapSize = { width: 0, height: 0 };

              if (mapElement) {
                mapSize.width = mapElement.clientWidth;
                mapSize.height = mapElement.clientHeight;
              }

              const {
                tasks: tasksReposition,
                areas: areasReposition,
                zones
              } = repositionImageToMap(
                updatedGameDocument.assets,
                mapEntity.longitude ?? DEFAULT_LONGITUDE,
                mapEntity.latitude ?? DEFAULT_LATITUDE,
                8,
                [mapSize.width, mapSize.height],
                imgDimention.width,
                imgDimention.height
              );

              const taskIds = tasks.map((t) => t.id);
              const areaIds = areas.map((a) => a.id);
              const filteredAreas = areasReposition.filter((a) =>
                areaIds.includes(a.id)
              );
              const filteredTasks = tasksReposition.filter((t) =>
                taskIds.includes(t.id)
              );

              //@ts-ignore
              updatedGameDocument.assets = {
                ...updatedGameDocument.assets,
                zones,
                areas: [
                  ...(response.assets.areas ?? []).filter(
                    (a) => !areaIds.includes(a.id)
                  ),
                  ...filteredAreas
                ],
                tasks: [
                  ...(response.assets.tasks ?? []).filter(
                    (t) => !taskIds.includes(t.id)
                  ),
                  ...filteredTasks
                ]
              };

              setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
            };
          } else {
            setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
          }
        }
      );
    },
    [setState, state?.gameDocument]
  );

  const calculateCenter = (geometry: any) => {
    let centerZone: any, coordinates;

    let x = 0;
    let y = 0;
    let i = 0;
    coordinates = geometry.coordinates[0];
    coordinates.forEach(function (coordinate: any) {
      x += coordinate[0];
      y += coordinate[1];
      i++;
    });
    centerZone = [x / i, y / i];
    return centerZone;
  };

  const handleZoneEntityEditorSubmit = useCallback(
    async (
      editorEntity: EntityEditor<ZoneEntity>,
      resourceEntity: EntityEditor<ResourceEntity>[]
    ) => {
      editorEntity.entity.boundary = tempZone.boundary;
      editorEntity.entity.id = uuid();
      editorEntity.entity.isVisible = true;
      if (state.gameDocument) {
        //add zone into Map
        const centerZone = calculateCenter(tempZone.boundary.geometry);
        const type =
          state.gameDocument.assets && state.gameDocument.assets.maps
            ? state.gameDocument.assets.maps[0].type
            : 'googleMaps';

        if (centerZone !== undefined) {
          const zoomLevel: number = 17;
          let zoneMap: MapEntity = {
            id: uuid(),
            name: editorEntity.entity.name,
            description: editorEntity.entity.description,
            type,
            latitude: type === 'illustrated' ? undefined : centerZone[1],
            longitude: type === 'illustrated' ? undefined : centerZone[0],
            zoomLevel: type === 'illustrated' ? undefined : zoomLevel
          };

          let resZone = AddMapEntityAsync(state.gameDocument, zoneMap);

          if (resZone !== undefined) {
            // add new resource
            AddResourceAsync(
              state.gameDocument!,
              `${editorEntity.entity.id}-title`,
              'Zone title',
              'text',
              resourceEntity.find(
                (res) => res.entity.id === editorEntity.entity.titleResId!
              )?.entity.value!,
              editorEntity.entity.titleResId
            ).then((response) => {
              setState((state) => UpdateGameDocState(state, response!));
            });

            let result = await AddZoneEntityAsync(
              state.gameDocument!,
              editorEntity.entity
            );
            if (result !== undefined) {
              result.rules.worldMap.zones.push({
                zoneAssId: editorEntity.entity.id,
                mapAssId: zoneMap.id
              });
              setState((prev) => UpdateGameDocState(prev, result));
              setEntityZoneEditorIsVisible(false);
            }
          }
        }
      }
    },
    [setState, state.gameDocument, tempZone.boundary]
  );

  const onAddZone = useCallback((zone: ZoneEntity) => {
    setTempZone({
      id: '',
      name: '',
      description: '',
      titleResId: '',
      boundary: {
        id: uuid(),
        type: 'Feature',
        geometry: {
          type: zone.boundary?.type ?? 'Polygon',
          coordinates: zone.boundary?.geometry as any
        }
      }
    });

    setEntityZoneEditorIsVisible(true);
  }, []);

  const onAddArea = (area: AreaEntity) => {
    setTempArea({
      id: uuidv4(),
      name: '',
      description: '',
      boundary: {
        id: uuidv4(),
        type: 'Feature',
        geometry: {
          type: area.boundary?.type ?? 'Polygon',
          coordinates: area.boundary?.geometry as any
        }
      }
    });
    toggleAreaEntityEditor();
  };

  const handleAreaEntityEditorSubmit = async (
    editorEntity: EntityEditor<AreaEntity>,
    resourceEntity: EntityEditor<ResourceEntity>[]
  ) => {
    editorEntity.entity.boundary = tempArea.boundary;
    editorEntity.entity.id = uuidv4();
    editorEntity.entity.isVisible = true;

    if (state.gameDocument) {
      // add new resource
      AddResourceAsync(
        state.gameDocument!,
        `${editorEntity.entity.id}-title`,
        'Area title',
        'text',
        resourceEntity.find(
          (res) => res.entity.id === editorEntity.entity.titleResId!
        )?.entity.value!,
        editorEntity.entity.titleResId
      ).then((response) => {
        setState((state) => UpdateGameDocState(state, response!));
      });

      await AddAreaEntityAsync(state.gameDocument, editorEntity.entity).then(
        (updatedGameDocument) => {
          setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
          toggleAreaEntityEditor();
        }
      );
    }
  };

  /**
   * Handle when area resize, move or edit
   * @param area
   */
  const handleEditAreaChange = (id: string, geometry: Geometry) => {
    setState((prev) => {
      const newCoordinate = (geometry as Polygon).getCoordinates();
      const areas = prev.gameDocument?.assets.areas ?? [];
      const selectedArea = areas.find((a) => a.boundary.id === id);

      if (selectedArea) {
        selectedArea.boundary.geometry.coordinates = newCoordinate;
        const resp = UpdateAreaAsync(prev.gameDocument!, id, selectedArea);
        return UpdateGameDocState(prev, resp);
      }

      return prev;
    });
  };

  const titleGuid = uuid();
  const defaultTaskAvailableIconResId =
    state.gameDocument?.settings.designer?.defaultTaskAvailableIconResId || '';
  const defaultTaskCompletedIconResId =
    state.gameDocument?.settings.designer?.defaultTaskCompletedIconResId || '';

  const entity: TaskEntity = {
    id: '',
    name: '',
    description: '',
    titleResId: titleGuid,
    imageResId: defaultTaskAvailableIconResId,
    completeImageResId: defaultTaskCompletedIconResId,
    hideWhenComplete:
      state.gameDocument && !state.gameDocument.settings.designer
        ? false
        : !Boolean(
            state.gameDocument?.settings.designer?.taskCompletionVisibility
          )
  };

  const titleResource: ResourceEntity = {
    id: titleGuid,
    name: 'Title',
    description: '',
    type: 'text',
    value: ''
  };

  const handleSelectedExistingTask = (taskId: string) => {
    let task = GetTaskById(state?.gameDocument!, taskId);
    if (task) {
      task!.boundary = tempTask.boundary;
      task.isVisible = true;

      UpdateTaskAsync(state?.gameDocument!, task.id, task).then((result) => {
        result.rules.worldMap.tasks.push({
          taskAssId: task!.id
        });
        setState((prev) => UpdateGameDocState(prev, result));
        setEntityTaskEditorIsVisible(false);
      });
    }
  };

  const handleTaskEntityEditorSubmit = useCallback(
    async (
      editorEntity: EntityEditor<TaskEntity>,
      resourceEntity: EntityEditor<ResourceEntity>[]
    ) => {
      const promises = [];

      const addTitleResource = AddResourceAsync(
        state.gameDocument!,
        'Title',
        '',
        'text',
        resourceEntity.find(
          (task) => task.entity.id === editorEntity.entity.titleResId!
        )?.entity.value!,
        editorEntity.entity.titleResId
      );
      promises.push(addTitleResource);

      if (
        editorEntity.entity.imageResId &&
        editorEntity.entity.imageResId !== defaultTaskAvailableIconResId
      ) {
        const imageResource = resourceEntity.find(
          (task) => task.entity.id === editorEntity.entity.imageResId!
        )?.entity;
        if (imageResource) {
          const addImageResource = AddResourceAsync(
            state.gameDocument!,
            imageResource.name || editorEntity.entity.imageResId!,
            imageResource.description ?? '',
            'image',
            imageResource.value ?? '',
            editorEntity.entity.imageResId!,
            imageResource.size ?? 0
          );
          promises.push(addImageResource);
        }
      }

      if (
        editorEntity.entity.completeImageResId &&
        editorEntity.entity.completeImageResId !== defaultTaskCompletedIconResId
      ) {
        const completeImageResource = resourceEntity.find(
          (task) => task.entity.id === editorEntity.entity.completeImageResId!
        )?.entity;
        if (completeImageResource) {
          const addCompleteImageResource = AddResourceAsync(
            state.gameDocument!,
            completeImageResource.name ||
              editorEntity.entity.completeImageResId!,
            completeImageResource.description ?? '',
            'image',
            completeImageResource.value ?? '',
            editorEntity.entity.completeImageResId!,
            completeImageResource.size ?? 0
          );
          promises.push(addCompleteImageResource);
        }
      }

      Promise.all(promises).then(() => {
        editorEntity.entity.boundary = tempTask.boundary;
        editorEntity.entity.id = uuid();
        editorEntity.entity.isVisible = true;

        if (state.gameDocument) {
          editorEntity.entity.isTitleVisible =
            state.gameDocument.settings.designer.defaultTaskTitleVisibility;
          AddActionToTask(state.gameDocument!, editorEntity.entity);
          AddTaskEntityAsync(state.gameDocument!, editorEntity.entity).then(
            (result) => {
              result.rules.worldMap.tasks.push({
                taskAssId: editorEntity.entity.id
              });
              setState((prev) => UpdateGameDocState(prev, result));
              setEntityTaskEditorIsVisible(false);
            }
          );
        }
      });
    },
    [
      defaultTaskAvailableIconResId,
      defaultTaskCompletedIconResId,
      tempTask.boundary,
      state.gameDocument,
      setState
    ]
  );

  const onAddTask = useCallback((coordinates: [number, number]) => {
    setTempTask({
      id: '',
      name: '',
      description: '',
      boundary: {
        id: uuid(),
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates
        }
      }
    });

    setEntityTaskEditorIsVisible(true);
  }, []);

  const centerToMyLocation = useCallback(() => {
    if (!isEmpty(state.gameDocument) && !isEmpty(state.gameDocument.assets)) {
      if (!isEmpty(state.gameDocument?.assets?.maps![0])) {
        setCoordinate({
          lat: state.gameDocument?.assets?.maps![0].latitude!,
          lng: state.gameDocument?.assets?.maps![0].longitude!
        });
        if (state.gameDocument?.assets?.maps![0].zoomLevel) {
          map.getView().setZoom(state.gameDocument?.assets?.maps![0].zoomLevel);
        }
      }
    }
  }, [map, state.gameDocument]);

  /**
   * Handle when zone resize, move or edit
   * @param worldZones
   */
  const handleEditZoneChange = (id: string, geometry: Geometry) => {
    setState((prev) => {
      const newCoordinate = (geometry as Polygon).getCoordinates();
      const zones = prev.gameDocument?.assets.zones ?? [];
      const selectedZone = zones.find((a) => a.boundary.id === id);

      if (selectedZone) {
        selectedZone.boundary.geometry.coordinates = newCoordinate;
        const resp = UpdateZone(prev.gameDocument!, id, selectedZone);
        return UpdateGameDocState(prev, resp);
      }

      return prev;
    });
  };

  /**
   * Handle World Map changes the coordinate
   * @param event
   */
  const getMapInfo = () => {
    const center = map.getView().getCenter();
    if (!center) return undefined;
    const latLon = toLonLat(center);
    const currentCoordinate: Coordinate = {
      lat: latLon[1],
      lng: latLon[0]
    };
    const zoom = map.getView().getZoom() || 4;
    const mapEntity: MapEntity = {
      titleResId: state.gameDocument?.assets?.maps![0].id,
      id: state.gameDocument?.assets?.maps![0].id || uuid(),
      longitude: currentCoordinate.lng,
      latitude: currentCoordinate.lat,
      name: state.gameDocument?.assets?.maps![0].name || '',
      description: state.gameDocument?.assets?.maps![0].description || '',
      type: state.gameDocument?.assets?.maps![0].type!,
      zoomLevel: zoom,
      imageResId: state.gameDocument?.assets?.maps![0]?.imageResId ?? ''
    };

    if (mapEntity.type === 'illustrated') mapEntity.zoomLevel = 1;
    return mapEntity;
  };

  const onMapMoveEnd = () => {
    const mapEntity = getMapInfo();
    setMapInfo(mapEntity);
  };

  const updateMapInfo = async () => {
    if (mapInfo) {
      UpdateMapAsync(state?.gameDocument!, mapInfo?.id, mapInfo).then(
        (response) => {
          setState((prev) => UpdateGameDocState(prev, response));
        }
      );
    }
  };

  const resetWorldMap = async (
    id: string,
    latitude?: number,
    longitude?: number
  ) => {
    const worldMap: MapEntity = GenerateDefaultWorldMap(
      id,
      latitude,
      longitude
    );

    await updateWorldMapAsset(worldMap);
    setMapInfo(worldMap);
    setCoordinate({
      lat: latitude ?? DEFAULT_LATITUDE,
      lng: longitude ?? DEFAULT_LONGITUDE
    });
  };

  const onChangeMapOptions = async (MapsOptions: MapsOptions) => {
    let mapEntity = await getWorldMapAsset();
    if (mapEntity) {
      if (MapsOptions === 'illustrated') {
        mapEntity.type = 'illustrated';
        mapEntity.zoomLevel = undefined;
        mapEntity.longitude = undefined;
        mapEntity.latitude = undefined;
        await updateWorldMapAsset(mapEntity);
        setMapInfo(mapEntity);
      } else {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              await resetWorldMap(mapEntity!.id, latitude, longitude);
            },
            async () => await resetWorldMap(mapEntity!.id, undefined, undefined)
          );
        } else {
          await resetWorldMap(mapEntity.id, undefined, undefined);
        }
      }
    }
  };

  const onEditTask = (task: TaskEntity) => {
    UpdateTaskAsync(state?.gameDocument!, task.id, task).then((response) => {
      setState((prev) => UpdateGameDocState(prev, response));
    });
  };

  const onEditMapImage = (mapImage: MapImage) => {
    UpdateMapImageAsync(state?.gameDocument!, mapImage.id, mapImage).then(
      (response) => {
        setState((prev) => UpdateGameDocState(prev, response));
      }
    );
  };

  const debounceFn = useMemo(() => debounce(onMapMoveEnd, 200), [onMapMoveEnd]);

  const mapImages = useMemo(() => {
    if (state.gameDocument) {
      if (zoneId) {
        return state?.gameDocument?.assets?.mapImages?.filter(
          (item) =>
            item.mapId ===
              state.gameDocument?.rules.worldMap.zones.find(
                (zone: ZoneAssetId) => zone.zoneAssId === zoneId
              )?.mapAssId || ''
        );
      } else {
        return (
          state?.gameDocument?.assets?.mapImages?.filter(
            (item) =>
              item.mapId === state?.gameDocument?.rules?.worldMap?.mapAssId!
          ) ?? []
        );
      }
    }
    return [];
  }, [state.gameDocument]);

  const getMapLabel = useMemo(() => {
    const worldMap = state.gameDocument?.assets?.maps?.[0];
    if (!worldMap) return 'gis';

    const mapType = worldMap.type ?? 'googleMaps';
    if (['googleMaps', 'openStreetMap', 'satelliteMap'].includes(mapType))
      return 'gis';

    return 'illustrated';
  }, [state.gameDocument?.assets.maps]);

  useEffect(() => {
    updateMapInfo();
  }, [mapInfo]);

  const imageLayersRef = useRef<Record<string, ImageLayer<GeoImage>>>({});

  const updateOverlayImage = (
    id: string,
    newCenter: [number, number],
    newExtent: [number, number, number, number],
    newScale: [number, number] | undefined
  ) => {
    if (state.gameDocument) {
      const source = imageLayersRef.current[id].getSource();
      if (source) {
        source.setCenter(newCenter);
        newScale && source.setScale(newScale);
      }

      const imageId = id.replaceAll('image-', '');
      const mapImage = mapImages?.find((image) => image.id === imageId);

      if (mapImage) {
        let result = cloneDeep(mapImage);
        result = {
          ...result,
          center: newCenter,
          extent: newExtent,
          ...(newScale !== undefined && {
            scale: newScale
          })
        };

        onEditMapImage(result);
      }
    }
  };

  const {
    taskPopupId,
    removeTaskOverlay,
    addTaskOverlay,
    updateTaskOverlayPosition
  } = useGameTask({
    showTaskTitle,
    selectAllAssets,
    tasks: tasks || [],
    zones: zones || [],
    areas: areas || [],
    mapImages: mapImages || [],
    mapType: state?.gameDocument?.assets?.maps![0].type || 'googleMaps',
    updateOverlayImage
  });

  const {} = useGameRoute();

  const { data: dataTheme } = useGameTheme(gameId ?? '');

  const iconScale: string = React.useMemo(() => {
    if (!dataTheme?.iconScale) return '75px';

    switch (dataTheme.iconScale) {
      case 'sm':
        return '75px';
      case 'md':
        return '125px';
      case 'lg':
        return '200px';
    }
  }, [dataTheme?.iconScale]);

  useEffect(() => {
    centerToMyLocation();
    const mapEntity = getMapInfo();
    setMapInfo(mapEntity);
  }, []);

  return (
    <>
      <div className={'map-designer'}>
        <div className={'layout__left'}>
          {state.gameDocument &&
            (state.gameDocument.assets?.maps![0].type === 'googleMaps' ||
              state.gameDocument.assets?.maps![0].type === 'openStreetMap' ||
              state.gameDocument.assets?.maps![0].type === 'satelliteMap') && (
              <MapCanvas
                setVectorPopupId={setVectorPopupId}
                mapInfo={mapInfo}
                coordinate={coordinate}
                showAllZones={showAllZones}
                isShowZoneNameVisibility={isShowZoneNameVisibility}
                showAllAreas={showAllAreas}
                isShowAreaNameVisibility={isShowAreaNameVisibility}
                zones={zones}
                tasks={tasks}
                areas={areas}
                routes={routes}
                resourceImages={state.gameDocument.resources}
                isClearInteractions={true}
                onMoveEnd={debounceFn}
                onEditZone={handleEditZoneChange}
                onAddZone={onAddZone}
                onAddTask={onAddTask}
                onAddArea={onAddArea}
                onEditArea={handleEditAreaChange}
                onEditTasks={onEditTask}
                onEditMapImages={onEditMapImage}
                selectedPanel={selectedPanel}
                mapImages={mapImages}
                selectedObjectType={(selectAllAssets as any) ?? ''}
                onRemoveSelectAllAssets={() => setSelectAllAssets('')}
                addTaskOverlay={addTaskOverlay as any}
                removeTaskOverlay={removeTaskOverlay}
                updateTaskOverlayPosition={updateTaskOverlayPosition as any}
                imageLayersRef={imageLayersRef}
                updateOverlayImage={updateOverlayImage}
              />
            )}
          {state.gameDocument &&
            state.gameDocument.assets?.maps![0].type === 'illustrated' && (
              <MapIllustrationCanvas
                zones={zones}
                tasks={tasks}
                routes={routes}
                setVectorPopupId={setVectorPopupId}
                showAllZones={showAllZones}
                isShowZoneNameVisibility={isShowZoneNameVisibility}
                onEditZone={handleEditZoneChange}
                onAddZone={onAddZone}
                onAddTask={onAddTask}
                onEditTasks={onEditTask}
                resourceImages={state.gameDocument.resources}
                url={GetResourceValue(
                  state.gameDocument,
                  state.gameDocument.assets?.maps![0].imageResId!
                )}
                selectedPanel={selectedPanel}
                isClearInteractions={true}
                mapImages={mapImages}
                onEditMapImages={onEditMapImage}
                selectedObjectType={(selectAllAssets as any) ?? ''}
                onRemoveSelectAllAssets={() => setSelectAllAssets('')}
                addTaskOverlay={addTaskOverlay as any}
                removeTaskOverlay={removeTaskOverlay}
                updateTaskOverlayPosition={updateTaskOverlayPosition as any}
                imageLayersRef={imageLayersRef}
                updateOverlayImage={updateOverlayImage}
              />
            )}
        </div>

        <div className={'layout__right'}>
          {state.gameDocument && (
            <MapEditor
              taskPopupId={taskPopupId}
              showTaskTitle={showTaskTitle}
              vectorPopupId={vectorPopupId}
              toggleShowTaskTitle={toggleShowTaskTitle}
              onChangeMapOptions={onChangeMapOptions}
              onSelectedPanel={onSelectedPanel}
              mapOptions={getMapLabel}
              showMapAccordion={true}
              showZonesAccordion={true}
              showAreasAccordion={true}
              showTasksAccordion={true}
              showAllZones={showAllZones}
              isShowZoneNameVisibility={isShowZoneNameVisibility}
              setIsShowZoneNameVisibility={setIsShowZoneNameVisibility}
              isShowAreaNameVisibility={isShowAreaNameVisibility}
              setIsShowAreaNameVisibility={setIsShowAreaNameVisibility}
              showAllAreas={showAllAreas}
              setShowAllAreas={setShowAllAreas}
              setShowAllZones={setShowAllZones}
              onCenterMap={setToBrowserLocation}
              mapInfo={mapInfo}
              zones={zones}
              currentImageResource={
                GetResourceValue(
                  state.gameDocument,
                  state.gameDocument.assets?.maps![0].imageResId!
                ) ?? ''
              }
              onSelectAllAssets={(value) => setSelectAllAssets(value)}
              selectedAllObjectType={selectAllAssets}
              removeTaskOverlay={removeTaskOverlay}
              updateTaskOverlayPosition={updateTaskOverlayPosition as any}
            />
          )}

          {entityZoneEditorIsVisible && (
            <ZoneEditorWindow
              toggleDialog={toggleZoneEntityEditor}
              onSubmit={handleZoneEntityEditorSubmit}
              editorEntity={{
                isNew: true,
                entity: {
                  id: '',
                  name: '',
                  description: '',
                  titleResId: titleGuid
                }
              }}
              editorMode={'basic'}
              editorResource={[
                {
                  isNew: true,
                  entity: titleResource
                }
              ]}
            />
          )}

          {entityAreaEditorIsVisible && (
            <AreaEditorWindow
              toggleDialog={toggleAreaEntityEditor}
              onSubmit={handleAreaEntityEditorSubmit}
              onClose={toggleAreaEntityEditor}
              editorEntity={{
                isNew: true,
                entity: {
                  id: '',
                  name: '',
                  description: '',
                  titleResId: titleGuid
                }
              }}
              editorMode={'basic'}
              editorResource={[
                {
                  isNew: true,
                  entity: titleResource
                }
              ]}
            />
          )}

          {entityTaskEditorIsVisible && (
            <TaskEditorWindow
              toggleDialog={toggleTaskEntityEditor}
              onSubmit={handleTaskEntityEditorSubmit}
              onSelectedExistingTask={handleSelectedExistingTask}
              editorEntity={{
                isNew: true,
                entity: entity
              }}
              editorMode={'basic'}
              editorResource={[
                {
                  isNew: true,
                  entity: titleResource
                }
              ]}
              showExistingTask={true}
            />
          )}
        </div>
      </div>

      {iconScale && (
        <style type={'text/css'}>
          {`
              .task-container {
                border-radius: ${iconScale} !important;
                .avatar-container,
                .icon-container {
                  .avatar-circle,
                  .icon-circle {
                    .avatar-img,
                    .icon-img {
                      height: ${iconScale} !important;
                      width: ${iconScale} !important;
                    }
                  }
                }
              }
              `}
        </style>
      )}
    </>
  );
};

export default WorldMap;
